import React from 'react';
import { CSSTransitionGroup } from '../../../../../../../../node_module_clones/react-transition-group';

import AiAssistantPopupResultElement from './AiAssistantPopupResultElement';
import AiAssistantPopupResultPromptHistory from './AiAssistantPopupResultPromptHistory';
import AiAssistantPopupResultBotHistory from './AiAssistantPopupResultBotHistory';
import AiAssistantPopupResultDebugInfo from './AiAssistantPopupResultDebugInfo';

import useUpdateResultsScrollTop from './useUpdateResultsScrollTop';

import {
    isIdeaBotHistory,
    isIdeaDebugInfo,
    isIdeaPromptHistory,
    isIdeaResponse,
    AiResultType,
} from '../../state/aiToolStateTypes';
import { AiPopupProps } from '../AiAssistantPopup';

import { AI_TRANSITION_GROUP_DURATION } from '../../state/aiToolStateConstants';

import './AiAssistantPopupResults.scss';

interface AiAssistantPopupResultsProps extends AiPopupProps {
    results: AiResultType[];
    fetched: boolean;
    fetching: boolean;
}

const AiAssistantPopupResultsContent = (props: AiAssistantPopupResultsProps): React.ReactElement | null => {
    const { results, currentBoardId, currentConversation } = props;
    const containerRef = React.useRef<HTMLDivElement>(null);

    useUpdateResultsScrollTop(containerRef, currentConversation);

    if (currentConversation?.fetching) return null;

    return (
        <div className="AiAssistantPopupResultsContent" ref={containerRef}>
            <div className="ul-wrapper">
                <ul>
                    <CSSTransitionGroup
                        transitionName="keep-around"
                        transitionEnterTimeout={1}
                        transitionLeaveTimeout={400}
                    >
                        {results.map((result) => (
                            <li key={result.uuid}>
                                {isIdeaPromptHistory(result) && <AiAssistantPopupResultPromptHistory result={result} />}
                                {isIdeaBotHistory(result) && <AiAssistantPopupResultBotHistory result={result} />}
                                {isIdeaDebugInfo(result) && <AiAssistantPopupResultDebugInfo result={result} />}
                                {isIdeaResponse(result) && (
                                    <AiAssistantPopupResultElement
                                        isDraggable
                                        currentBoardId={currentBoardId}
                                        result={result}
                                    />
                                )}
                            </li>
                        ))}
                    </CSSTransitionGroup>
                    {currentConversation?.error && (
                        <li>
                            <div className="error-message">{currentConversation.error}</div>
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
};

/**
 * If we're fetching or we don't have results, use an "empty" key to ensure the transition in of
 *  the results is in-sync with the transition in of the prompts.
 * NOTE: New conversations will also fade in, rather than animating in from outside of the popup
 */
const getResultsContentKey = (props: AiAssistantPopupResultsProps): string => {
    const { currentConversation, results } = props;

    if (!currentConversation?.fetched || currentConversation?.fetching) return 'fetching';

    if (results.length === 0) return 'empty';

    return 'results';
};

const AiAssistantPopupResults = (props: AiAssistantPopupResultsProps): React.ReactElement => {
    const { transitionDirection } = props;

    const key = getResultsContentKey(props);

    return (
        <div className="AiAssistantPopupResults">
            <CSSTransitionGroup
                transitionName={`fade-${transitionDirection}`}
                transitionEnterTimeout={AI_TRANSITION_GROUP_DURATION}
                transitionLeaveTimeout={AI_TRANSITION_GROUP_DURATION}
            >
                <AiAssistantPopupResultsContent {...props} key={key} />
            </CSSTransitionGroup>
        </div>
    );
};

/**
 * NOTE: This multi-sub-component CSSTransitionGroup structure is intentional to ensure the transitions remain
 *  in-sync with the prompts.
 * The prompts show a loading state when the prompts aren't loaded yet, so in order to transition in the results
 *  at the same time as the prompts, we need to ensure there's a second CSSTransitionGroup that will transition
 *  in when the results are loaded.
 */
const AiAssistantPopupResultsTransitionContainer = (props: AiAssistantPopupResultsProps): React.ReactElement => (
    <CSSTransitionGroup
        transitionName={`fade-${props.transitionDirection}`}
        transitionEnterTimeout={AI_TRANSITION_GROUP_DURATION}
        transitionLeaveTimeout={AI_TRANSITION_GROUP_DURATION}
    >
        <AiAssistantPopupResults {...props} key={props.currentConversation?._id || 'loading'} />
    </CSSTransitionGroup>
);

export default AiAssistantPopupResultsTransitionContainer;
